import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import bindAllOfThis from "src/utils/BindThisHelper";
import { Candidate } from '../objects/Candidate';
import { Career, NPCResponse, InterviewDialogue } from '../objects/Career';
import { Booth } from '../objects/Booth';
import { PhaserModal, PhaserModalConfig, ModalOption } from 'src/games/shared/Modal';
import { PhaserModalTitle, PhaserModalTitleConfig, ModalTitleOption } from 'src/games/shared/ModalTitle';
import { PhaserButton, PhaserButtonConfig } from 'src/games/shared/Button';
import { PhaserGamePad } from "src/games/shared/Gamepad";
import TranslateApi from "src/utils/TranslateHelper";
import initializeInteractiveCursor from "src/games/shared/InteractiveCursorUtil";
import { Color } from "src/games/shared/ColorValue";
import { EndGameScene } from "src/games/CareerFairGame/scenes/EndGameScene";
import AudioTranslations from "src/constants/AudioTranslations";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    player: Phaser.Physics.Arcade.Sprite;
    candidates: Candidate[] = Candidate.CANDIDATES;
    groundLayer: Phaser.Tilemaps.StaticTilemapLayer;
    borderLayer: Phaser.Tilemaps.ObjectLayer;
    candidateLayer: Phaser.Tilemaps.ObjectLayer;
    boothLayer: Phaser.Tilemaps.ObjectLayer;
    doubleJump: boolean = true;
    careers = Career.CAREERS;
    astronaut: Phaser.Physics.Arcade.Image;
    currentCareer: Career = null;
    booths: Booth[] = [];
    gamePad: PhaserGamePad;
    currentCandidate: Candidate = null;
    currentBooth: Booth = null;
    playerAlwaysLeads: boolean = false;
    boothModal: PhaserModalTitle = null;
    astronautModal: PhaserModal;
    questionModal: PhaserModal;
    responseModal: PhaserModal;
    correctCandidateModal: PhaserModal;
    frameCount: number = 0;
    stopMovement: boolean = false;

    xVel: number = 0;
    yVel: number = 0;

    private JUMPING_VELOCITY = 400;
    private WALKING_VELOCITY = 200;

    private modalConfig: PhaserModalConfig = {
        buttonWidth: 200,
        title: "",
        buttonConfig: new PhaserButtonConfig()
    };

    private modalTitleConfig: PhaserModalTitleConfig = {
        borderColor: 0xffae00,
        buttonWidth: 100,
        buttonConfig: new PhaserButtonConfig()
    };

    private translateString = "CareerFair.Phaser.MainScene.";

    constructor() {
        super({
            key: "MainScene" + Math.random()
        });
    }

    preload(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.load.image('blocks', require("/src/assets/qci/tilemaps/tiles/blocks.png"));
        this.load.image('background', require("/src/assets/qci/backgrounds/career_fair_bg.jpg"));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/career_fair.json"));
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.image("exclamation-point", require("/src/assets/qci/sprites/exclamation-point-icon.png"));
        this.load.spritesheet('player', require('src/assets/qci/sprites/studentPilot/student_spritemap.png'), { frameWidth: 32, frameHeight: 48 });
        this.load.atlas('assets', require("/src/assets/qci/atlas/gameCareerFair.png"), require("/src/assets/qci/atlas/gameCareerFair.json"));
        this.load.atlas('pilots', require("/src/assets/qci/atlas/starPilotAll.png"), require("/src/assets/qci/atlas/starPilotAll.json"));

        var code = TranslateApi.currentLanguage.code;
        var keys = [
            "MainScene.correctCandidate",
            "MainScene.incorrectCandidate",
        ];
        // All the careers audio
        for (var i = 1; i < 5; i++) {
            keys.push("Careers." + i + ".boothText.1");
            keys.push("Careers." + i + ".boothText.2");
            keys.push("Careers." + i + ".question1");
            keys.push("Careers." + i + ".question2");
            keys.push("Careers." + i + ".responses1.1");
            keys.push("Careers." + i + ".responses1.2");
            keys.push("Careers." + i + ".responses1.3");
            keys.push("Careers." + i + ".responses2.1");
            keys.push("Careers." + i + ".responses2.2");
            keys.push("Careers." + i + ".responses2.3");
        }

        keys.forEach((subkey) => {
            this.load.audio("CareerFair.Phaser." + subkey, AudioTranslations.FindByKeyAndLanguage("CareerFair.Phaser." + subkey, code).url);
        });

        PhaserGamePad.preload(this);
        PhaserButton.preload(this);
        PhaserModal.preload(this);
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.initializeMap();
        this.generateScene();
    }

    // Initializes the tilemap and sets tile collision properties
    initializeMap(): void {
        this.map = this.make.tilemap({ key: 'map' });
        var groundTiles = this.map.addTilesetImage('blocks');
        var backgroundTiles = this.map.addTilesetImage('background');
        this.map.createStaticLayer('Background Layer', backgroundTiles, 0, 0);
        this.groundLayer = this.map.createStaticLayer('Ground Layer', groundTiles, 0, 0);
        this.borderLayer = this.map.getObjectLayer("Borders");
        this.candidateLayer = this.map.getObjectLayer('Candidate Layer');
        this.boothLayer = this.map.getObjectLayer('Booth Layer');

        this.groundLayer.setCollisionBetween(this.groundLayer.tileset.firstgid, this.groundLayer.tileset.firstgid + 20);
    }

    setCollisions(): void {
        this.borderLayer.objects.forEach((object: TiledGameObject) => {
            // The group will have an array of objects - these are the individual collision shapes
            var gameObject = new Phaser.GameObjects.Zone(this, object.x, object.y, object.width, object.height);
            gameObject.type = object.type;
            this.physics.add.existing(gameObject, true);
            this.physics.add.collider(this.player, gameObject);
            this.physics.add.collider(this.astronaut, gameObject);
            this.candidates.forEach((c: Candidate) => {
                this.physics.add.collider(c.object, gameObject);
            });
        });
    }

    generateScene() {
        this.generatePlayer();
        this.generateNPCs();
        this.setCollisions();
        this.generateBooths();
        this.createGamePad();

        // Setup the camera to follow the player
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player);
        initializeInteractiveCursor(this);
    }

    generatePlayer(): void {
        this.player = this.physics.add.sprite(640, 630, "player");
        this.player.setBounce(0.2);
        this.player.setDepth(1);
        this.player.setFrame(4);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'player', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });

        this.physics.add.collider(this.player, this.groundLayer);
    }

    /**
     * Generates the astronaut and candidates with their careers
     * */
    generateNPCs(): void {
        var i = 0;
        // Scramble candidates so that they're not always in the same place
        this.candidates = this.scrambleArray(this.candidates);
        this.candidateLayer.objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "astronaut") {
                this.astronaut = this.physics.add.image(ob.x, ob.y, "pilots", "e");
                this.astronaut.setDisplaySize(32, 48);
                this.physics.add.collider(this.astronaut, this.groundLayer);
                this.physics.add.overlap(this.astronaut, this.player);
                this.astronaut.setDataEnabled();
                this.astronaut.setData('enabled', true);
            }
            else {
                var candidateObject;
                if (this.candidates[i].actualCareer == null) {
                    candidateObject = this.physics.add.sprite(ob.x, ob.y, "player");
                    candidateObject.setTint(0xff00000);
                    candidateObject.setDisplaySize(32, 48);
                    this.candidates[i].object = candidateObject;
                }
                else {
                    candidateObject = this.physics.add.sprite(ob.x, ob.y, "player");
                    candidateObject.setTint(0x4286f4);
                    candidateObject.setDisplaySize(32, 48);
                    candidateObject.setDataEnabled();
                    candidateObject.setData('position', new Phaser.Geom.Point(ob.x, ob.y));
                    this.candidates[i].object = candidateObject;
                }
                i++;
                candidateObject.setFrame(4);
                this.physics.add.collider(candidateObject, this.groundLayer);
                this.physics.add.overlap(candidateObject, this.player);
            }
        });
    }

    generateBooths(): void {
        var careers = [...this.careers];
        this.boothLayer.objects.forEach((ob: TiledGameObject) => {
            let image = this.physics.add.staticImage(ob.x, ob.y, "assets", ob.type);
            var career = careers.find((c: Career) => {
                return c.id === ob.type;
            });
            let booth = new Booth(image, career);
            booth.object.setOrigin(0);
            booth.object.setSize(ob.width, ob.height);
            booth.object.setDisplaySize(ob.width, ob.height);
            this.booths.push(booth);
        });
    }

    /**
     * Called when player overlaps with a booth
     */
    insideBooth(boothImage): void {
        var booth = this.booths.find((b: Booth) => {
            return b.object === boothImage;
        });
        booth.playerEnabled = false;

        // Only show modal if we aren't already looking for a candidate
        if (this.currentCareer == null && !booth.isComplete && !this.isModalOnScreen()) {
            var text1 = TranslateApi.translate(booth.career.boothText + ".1");
            var text2 = TranslateApi.translate(booth.career.boothText + ".2");
            var audio1 = this.sound.add(booth.career.boothText + ".1");
            audio1.play();
            var audio2 = this.sound.add(booth.career.boothText + ".2");
            audio2.play("", {
                delay: audio1.totalDuration
            } as SoundConfig);
            var modalConfig = this.modalTitleConfig;
            modalConfig.title = text1;
            modalConfig.bodyText = text2;
            modalConfig.buttonConfig.backgroundColor = Color.KUDER_PRIMARY_BUTTON;
            var ok = TranslateApi.translate("common.ok");
            var modalOptions = [
                { displayText: ok, id: 1 },
            ];

            this.boothModal = new PhaserModalTitle(this, 0, 0, this.modalTitleConfig, modalOptions);
            this.boothModal.setPosition(this.gameWidth / 2 - this.boothModal.width / 2, this.gameHeight / 2 - this.boothModal.height / 2);
            this.boothModal.on('optionClicked', (op: ModalOption) => {
                this.currentCareer = booth.career;
                this.currentBooth = booth;
                booth.object.setAlpha(0.7);
                this.boothModal.destroy();
                this.boothModal = null;
            });
        }
    }

    /**
     * Player interaction with the candidate
     * Ask questions, get responses, decide to choose/not choose the candidate
     */
    talkToCandidate(candidate: Candidate): void {
        if (!candidate.enabled) {
            return;
        }
        // Only able to talk to a candidate if you are already helping a booth, there's no modal already on screen, and nobody is following you
        if (this.currentCareer != null && !this.isModalOnScreen() && this.currentCandidate == null) {
            var questionModalConfig = this.modalConfig;
            var text = TranslateApi.translate(this.translateString + "ask");
            var booth = this.booths.find((b: Booth) => {
                return b.career.title === this.currentCareer.title;
            });

            // First modal asks player to ask a question
            questionModalConfig.title = text;
            questionModalConfig.buttonConfig.backgroundColor = Color.KUDER_SECONDARY_BUTTON;
            questionModalConfig.buttonWidth = 300;
            questionModalConfig.buttonConfig.smartSize = true;

            var dialogs = [this.currentCareer.interviewDialog[0], this.currentCareer.interviewDialog[1]];
            var modalOptions: ModalOption[] = [
                { displayText: TranslateApi.translate(dialogs[0].question), id: 1, object: dialogs[0] },
                { displayText: TranslateApi.translate(dialogs[1].question), id: 2, object: dialogs[1] },
                { displayText: TranslateApi.translate(this.translateString + "exit"), id: 3 },
            ];


            var audio1 = this.sound.add(dialogs[0].question);
            audio1.play();
            var audio2 = this.sound.add(dialogs[1].question);
            audio2.play("", {
                delay: audio1.totalDuration
            });

            this.questionModal = new PhaserModal(this, 0, 0, modalOptions, questionModalConfig);
            // Over gamepad!
            this.questionModal.setDepth(1001);
            this.moveModalNextToPlayer(this.questionModal);
            this.questionModal.on('optionClicked', (op: ModalOption) => {
                if (op.id !== 3) {
                    this.questionModal.setVisible(false);
                    // Ask question
                    var dialog = op.object;
                    var responseKey = this.getCandidateResponse(candidate, dialog);
                    var response = TranslateApi.translate(responseKey);
                    // Create new modal on top with candidate's response added
                    var responseModalConfig = this.modalConfig;
                    responseModalConfig.title = response;

                    var audio3 = this.sound.add(responseKey);
                    audio3.play();

                    var modalOptions;
                    if (candidate.actualCareer == null) {
                        // Just have them move on
                        modalOptions = [
                            { displayText: TranslateApi.translate(this.translateString + "try"), id: 3 },
                        ];
                        responseModalConfig.buttonWidth = 300;
                    }
                    else {
                        modalOptions = [
                            { displayText: TranslateApi.translate(this.translateString + "follow"), id: 1 },
                            { displayText: TranslateApi.translate("common.back"), id: 2 },
                        ];
                        responseModalConfig.buttonWidth = 200;
                    }
                    this.responseModal = new PhaserModal(this, 0, 0, modalOptions, responseModalConfig);
                    // Over gamepad!
                    this.responseModal.setDepth(1001);
                    this.moveModalNextToPlayer(this.responseModal);
                    this.responseModal.on('optionClicked', (op: ModalOption) => {
                        this.responseModal.destroy();
                        this.questionModal.setVisible(true);
                        if (op.id === 1) {
                            // Now they will start following player
                            this.currentCandidate = candidate;
                            this.children.bringToTop(this.currentCandidate.object);
                            this.children.bringToTop(this.player);
                            this.questionModal.destroy();

                        }
                        else if (op.id === 3) {
                            this.questionModal.destroy();
                            this.responseModal.destroy();
                        }
                    });
                }
                else {
                    // Exit button
                    this.questionModal.destroy();
                }
            });
        }
    }

    getCandidateResponse(candidate: Candidate, dialog: InterviewDialogue): string {
        var response;
        // Not a candidate, just a filler person
        if (candidate.actualCareer == null) {
            response = this.getCandidateResponseText(dialog, null);
        }
        else {
            if (candidate.actualCareer.title === this.currentCareer.title) {
                // Answer positively, they are the correct candidate
                response = this.getCandidateResponseText(dialog, true);
            }
            else {
                // Try to trick the player by making them answer positively first, then negatively
                if (candidate.tryToTrickPlayer) {
                    response = this.getCandidateResponseText(dialog, true);
                    candidate.tryToTrickPlayer = false;
                }
                else {
                    response = this.getCandidateResponseText(dialog, false);
                }
            }
        }
        return response;
    }

    getCandidateResponseText(dialog: InterviewDialogue, value: boolean): string {
        var response = dialog.responses.find((r: NPCResponse) => {
            return r.isCorrect === value;
        });
        return response.text;
    }

    /**
     * Checks if the current candidate is a match for the current booth/career, shows feedback
     */
    checkCandidate(): void {
        if (!this.isModalOnScreen() && this.currentCandidate != null) {
            this.stopMovement = true;
            var candidate = this.candidates.find((c: Candidate) => {
                return c.object === this.currentCandidate.object;
            });
            this.currentBooth.candidateEnabled = false;
            var modalConfig = this.modalConfig;
            if (this.isCorrectCandidate(candidate)) {

                let audio = this.sound.add(this.translateString + "correctCandidate");
                audio.play();

                // Put player on the ground and disable them
                this.player.y = this.currentBooth.object.getBottomLeft().y - this.player.displayHeight / 2;
                this.player.anims.play('turn');
                this.player.disableBody();

                this.putCandidateInBooth();
                candidate.assignedCareer = this.currentCareer;
                modalConfig.title = TranslateApi.translate(this.translateString + "correctCandidate");
                this.correctCandidateModal = new PhaserModal(this, 0, 0, [{ displayText: TranslateApi.translate("common.ok"), id: 1 }], modalConfig);
                // Over gamepad!
                this.correctCandidateModal.setDepth(1001);
                this.correctCandidateModal.setPosition(this.cameras.main.width / 2 - this.correctCandidateModal.width / 2, this.cameras.main.height / 2 - this.correctCandidateModal.height / 2);
                this.correctCandidateModal.on('optionClicked', () => {
                    this.correctCandidateModal.destroy();
                    this.player.enableBody(false, this.player.x, this.player.y, true, true);
                    this.stopMovement = false;
                });
            }
            else {
                let audio = this.sound.add(this.translateString + "incorrectCandidate");
                audio.play();
                // Put candidate on the ground and disable them
                this.currentCandidate.object.y = this.currentBooth.object.getBottomLeft().y - this.currentCandidate.object.displayHeight / 2;
                this.currentCandidate.object.anims.play('turn');
                this.currentCandidate.object.disableBody();

                modalConfig.title = TranslateApi.translate(this.translateString + "incorrectCandidate");
                var incorrectCandidateModal = new PhaserModal(this, 0, 0, [{ displayText: TranslateApi.translate("common.ok"), id: 1 }], modalConfig);
                incorrectCandidateModal.setPosition(this.cameras.main.width / 2 - incorrectCandidateModal.width / 2, this.cameras.main.height / 2 - incorrectCandidateModal.height / 2);
                // Over gamepad!
                incorrectCandidateModal.setDepth(1001);
                incorrectCandidateModal.on('optionClicked', () => {
                    this.currentCandidate = null;
                    incorrectCandidateModal.destroy();
                    this.stopMovement = false;
                    // "Teleport" the candidate back to it's original position
                    this.tweens.add({
                        targets: candidate.object,
                        duration: 1000,
                        ease: "Power2",
                        alpha: {
                            getStart: () => 1,
                            getEnd: () => 0
                        },
                        onComplete: () => {
                            var originalPosition = candidate.object.getData('position');
                            // Put them back where they started
                            candidate.object.setPosition(originalPosition.x, originalPosition.y);
                            this.tweens.add({
                                targets: candidate.object,
                                duration: 1000,
                                ease: "Power2",
                                alpha: {
                                    getStart: () => 0,
                                    getEnd: () => 1
                                },
                                onStart: () => {
                                    candidate.object.enableBody(false, originalPosition.x, originalPosition.y, true, true);
                                }
                            });
                        }
                    });
                });
            }
        }
    }

    /**
     * Put candidate in center of booth and disable them from leaving
     */
    putCandidateInBooth(): void {
        var rightSideOfBooth = this.currentBooth.object.getBottomRight().x + 5;
        var boothFloor = this.currentBooth.object.getBottomLeft().y - this.currentCandidate.object.displayHeight / 2;
        this.add.tween({
            targets: this.currentCandidate.object,
            x: rightSideOfBooth,
            y: boothFloor,
            ease: Phaser.Math.Easing.Circular,
            onStart: () => {
                // Disable the candidate so they can't jump out of box before the tween starts
                this.currentCandidate.object.disableBody();
            },
            onComplete: () => {
                // Set current career to null so they know they need to go back to a booth
                this.currentCandidate.object.setVelocityX(0);
                this.currentCandidate.object.anims.play('turn');
                this.currentCandidate.enabled = false;
                this.currentCareer = null;
                this.currentCandidate = null;
                this.currentBooth.isComplete = true;
                this.stopMovement = false;
            }
        });
    }

    /**
     * Astronaut is the end trigger. Checks if the game is over or not, let's player move on
     * */
    talkToAstronaut(): void {
        this.astronaut.setData('enabled', false);
        if (!this.isModalOnScreen()) {
            var x, y;
            x = this.gameWidth - 200;
            y = this.astronaut.y - 20;
            var text, buttonText, text1, text2, id;
            id = 0;
            var modalConfig = this.modalConfig;
            if (this.isGameOver()) {
                text = TranslateApi.translate(this.translateString + "astronaut.doneText");
                buttonText = TranslateApi.translate("common.done");
                id = 0;
            }
            else {
                // Find out how many booths need employees
                var numNotAssigned = 0;
                this.booths.forEach((c) => {
                    if (!c.isComplete) {
                        numNotAssigned++;
                    }
                });
                if (numNotAssigned === 1) {
                    text1 = TranslateApi.translate(this.translateString + "astronaut.keepGoing1_singular");
                    text2 = TranslateApi.translate(this.translateString + "astronaut.keepGoing2_singular");
                }
                else {
                    text1 = TranslateApi.translate(this.translateString + "astronaut.keepGoing1_plural");
                    text2 = TranslateApi.translate(this.translateString + "astronaut.keepGoing2_plural");
                }
                text = text1 + numNotAssigned + text2;
                buttonText = TranslateApi.translate("common.ok");
                x = this.gameWidth - 350;
                id = 1;
            }
            modalConfig.buttonWidth = 150;
            modalConfig.buttonConfig.smartSize = true;
            modalConfig.title = text || null;

            this.astronautModal = new PhaserModal(this, x, y, [{ displayText: buttonText, id: id }], modalConfig);
            this.moveModalNextToPlayer(this.astronautModal);
            // Over gamepad!
            this.astronautModal.setDepth(1001);
            this.astronautModal.on('optionClicked', (op: ModalOption) => {
                this.astronautModal.destroy();
                if (op.id === 0) {
                    this.player.disableBody();
                    this.input.setDefaultCursor("default");
                    this.input.removeAllListeners();
                    this.scene.add("EndGameScene", EndGameScene, true);
                    this.scene.remove(this);
                }
            });
        }
    }

    /**
     * Handles user input
     * */
    update(): void {
        this.updatePlayerMovement();
        if (this.currentCandidate != null) {
            this.updateNPC();
        }
        this.checkIfCandidateIsInBooth();
        this.checkIfPlayerIsInBooth();
        this.checkIfPlayerIsNextToAstronaut();
        this.closeAbandonedModals();
    }

    checkIfPlayerIsNextToAstronaut(): void {
        var area = this.astronaut.getBounds();
        var rect = new Phaser.Geom.Rectangle(area.x, area.y, 50, 50);
        rect.centerX = this.astronaut.getCenter().x;
        rect.centerY = this.astronaut.getCenter().y;
        if (Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds()) && this.astronaut.getData('enabled')) {
            this.talkToAstronaut();
        }
        if (!Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds()) && !this.astronaut.getData('enabled')) {
            // Once they leave the astronaut, set it as enabled again so they can keep checking the game state/end the game
            this.astronaut.setData('enabled', true);

            if (this.isModalOnScreen()) {
                this.astronautModal.destroy();
                this.sound.stopAll();
            }
        }
    }

    closeAbandonedModals(): void {
        var closeModal = true;
        this.candidates.forEach((c: Candidate) => {
            var rect = c.object.getBounds();
            rect.width = 100;
            rect.height = 100;
            rect.centerX = c.object.getCenter().x;
            rect.centerY = c.object.getCenter().y;
            if (Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds())) {
                closeModal = false;
            }
        });

        if (this.boothModal != null) {
            return;
        }

        if (this.isModalOnScreen() && closeModal && this.questionModal != null) {
            this.questionModal.destroy();
            this.questionModal = null;
            this.sound.stopAll();
        }
        if (this.isModalOnScreen() && closeModal && this.responseModal != null) {
            this.responseModal.destroy();
            this.responseModal = null;
            this.sound.stopAll();
        }
    }

    /**
     * If the current candidate is inside the current booth (the player chose them and now we need to check if they were correct or not)
     */
    checkIfCandidateIsInBooth(): void {
        if (this.currentBooth != null && this.currentCandidate != null) {
            if (this.currentBooth.object.getBounds().contains(this.currentCandidate.object.x, this.currentCandidate.object.y) && this.currentBooth.candidateEnabled) {
                this.checkCandidate();
            }
            if (!this.currentBooth.object.getBounds().contains(this.currentCandidate.object.x, this.currentCandidate.object.y) && !this.currentBooth.candidateEnabled) {
                this.currentBooth.candidateEnabled = true;
            }
        }
    }

    /**
     * If player is in booth then call inside booth, otherwise enable the booth so that next time they're in it the modal will pop up again
     */
    checkIfPlayerIsInBooth(): void {
        for (var booth of this.booths) {
            if ((Phaser.Geom.Intersects.RectangleToRectangle(booth.object.getBounds(), this.player.getBounds()) && booth.playerEnabled)) {
                this.insideBooth(booth.object);
            }

            if (!Phaser.Geom.Intersects.RectangleToRectangle(booth.object.getBounds(), this.player.getBounds()) && !booth.playerEnabled) {
                // Once they leave the booth, set it as enabled again
                booth.playerEnabled = true;
                if (this.isModalOnScreen()) {
                    this.boothModal.destroy();
                    this.boothModal = null;
                    this.sound.stopAll();
                }
            }
        }
    }

    updateNPC(): void {
        if (this.currentCandidate != null && this.currentCandidate.object.body.enable) {
            if (this.player.body.velocity.x < 0) {
                this.currentCandidate.object.anims.play('left', true);
                this.physics.moveTo(this.currentCandidate.object, this.player.x + 32, this.player.y, this.WALKING_VELOCITY);
            }
            else if (this.player.body.velocity.x > 0) {
                this.currentCandidate.object.anims.play('right', true);
                this.physics.moveTo(this.currentCandidate.object, this.player.x - 32, this.player.y, this.WALKING_VELOCITY);
            }
            else {
                this.currentCandidate.object.anims.play('turn', true);
            }

            this.currentCandidate.object.y = this.player.y;
        }
    }

    isGameOver(): boolean {
        for (let c of this.candidates) {
            if (c.actualCareer != null && c.assignedCareer == null) {
                return false;
            }
            else if (c.actualCareer != null && c.assignedCareer != null) {
                if (c.actualCareer.title !== c.assignedCareer.title) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * Moves the modal either to the left or right of the player, while ensuring it doesn't run off the screen
     */
    moveModalNextToPlayer(modal: PhaserModal): void {
        var x, y;
        var padding = 30;

        var actualCandidateX = this.player.getBottomRight().x - this.cameras.main.scrollX;
        x = actualCandidateX;
        y = this.gameHeight / 2 - modal.height / 2;

        // Running off the right side of the screen, so put the modal on the left of the npc
        if (actualCandidateX + modal.width + padding > (this.gameWidth - padding)) {
            var maxX = this.gameWidth - modal.width - padding;
            x = Math.min(maxX, this.player.getBottomLeft().x - this.cameras.main.scrollX - modal.width);
        }

        modal.setPosition(x, y);
    }

    /**
     * Checks if there is already a modal open on screen (we don't want to be able to add more than one modal to a scene)
     */
    isModalOnScreen(): boolean {
        for (var c of this.children.list) {
            if (c.type === "PhaserModal") {
                return true;
            }
        }
        return false;
    }

    /**
     * Check if they assigned the candidate correctly
     * */
    isCorrectCandidate(candidate): boolean {
        if (candidate.actualCareer.title === this.currentCareer.title) {
            return true;
        }
        else {
            return false;
        }
    }

    /**
    * Shuffle the candidates so they're not in the same order every time
    * https://stackoverflow.com/questions/6274339/how-can-i-shuffle-an-array
    */
    scrambleArray(array) {
        var j, x, i;
        for (i = array.length - 1; i > 0; i--) {
            j = Math.floor(Math.random() * (i + 1));
            x = array[i];
            array[i] = array[j];
            array[j] = x;
        }
        return array;
    }


    /**
      * GamePad/Player Movement
      * EVENT BASED FOR JUMPING, UPDATE BASED FOR LEFT/RIGHT
      */
    createGamePad(): void {
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_up", this.playerJump);
        this.gamePad.on("keydown_a", this.playerJump);
        this.gamePad.on("keydown_b", this.bDown);
    }

    bDown() {
        this.candidates.forEach((c) => {
            // If you're close enough, talk to nearest character
            if ((Math.abs(c.object.x - this.player.x) / 32 < 2) && (Math.abs(c.object.y - this.player.y) / 32 < 2)) {
                this.talkToCandidate(c);
            }
        });

        // If you're close enough, talk to astronaut
        if ((Math.abs(this.astronaut.x - this.player.x) / 32 < 2) && (Math.abs(this.astronaut.y - this.player.y) / 32 < 2)) {
            this.talkToAstronaut();
        }

        if (this.boothModal && this.boothModal.active) {
            this.boothModal.selectLastOption();
        }

        if (this.correctCandidateModal && this.correctCandidateModal.active) {
            this.correctCandidateModal.selectLastOption();
        }
    }

    playerJump() {
        if (!this.stopMovement && this.player.body.enable) {
            if (this.player.body.onFloor()) {
                this.player.body.setVelocityY(-this.JUMPING_VELOCITY);
            } else if (this.doubleJump) {
                this.doubleJump = false;
                this.player.body.setVelocityY(-this.JUMPING_VELOCITY);
            }
        }
    }

    moveLeft() {
        if (!this.stopMovement && this.player.body.enable) {
            this.xVel += -this.WALKING_VELOCITY;
            this.player.anims.play('left', true);
        }

        if (this.currentCandidate != null && this.currentCandidate.object.body.enable) {
            this.currentCandidate.object.setPosition(this.player.x + 32, this.player.y);
        }
    }


    moveRight() {
        if (!this.stopMovement && this.player.body.enable) {
            this.xVel += this.WALKING_VELOCITY;
            this.player.anims.play('right', true);
        }

        if (this.currentCandidate != null && this.currentCandidate.object.body.enable) {
            this.currentCandidate.object.setPosition(this.player.x - 32, this.player.y);
        }
    }

    updatePlayerMovement(): void {
        this.xVel = 0;

        if (this.gamePad.keys.left.isDown) {
            this.moveLeft();
        }
        if (this.gamePad.keys.right.isDown) {
            this.moveRight();
        }

        this.player.setVelocityX(this.xVel);

        if (this.xVel === 0) {
            this.player.anims.play('turn');
            if (this.currentCandidate != null && this.currentCandidate.object.body.enable) {
                if (this.player.x > this.currentCandidate.object.x) {
                    // NPC is on the left of the player
                    this.currentCandidate.object.setPosition(this.player.x - 32, this.player.y);
                }
                else {
                    // NPC is on the right of the player
                    this.currentCandidate.object.setPosition(this.player.x + 32, this.player.y);
                }
            }
        }

        if (this.frameCount % 4 === 0) {
            if (this.player.body.onFloor()) {
                this.doubleJump = true;
            }
        }
        if (this.frameCount > 64000) {
            this.frameCount = 0;
        }
        this.frameCount++;
    }

    getActiveModal(): PhaserModal {
        let modal: PhaserModal;
        if (this.questionModal && this.questionModal.active) {
            modal = this.questionModal;
        }

        if (this.responseModal && this.responseModal.active) {
            modal = this.responseModal;
        }

        if (this.astronautModal && this.astronautModal.active) {
            modal = this.astronautModal;
        }

        if (this.correctCandidateModal && this.correctCandidateModal.active) {
            modal = this.correctCandidateModal;
        }

        return modal;
    }

    selectModalOption(modal: PhaserModal, index: number): void {
        if (!modal) {
            return;
        }

        if (index < 0) {
            modal.selectLastOption();
        }
        else {
            modal.selectOption(index);
        }
    }
}



// WEBPACK FOOTER //
// ./src/games/CareerFairGame/scenes/MainScene.ts